import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
    MdOutlineAdsClick,
    MdOutlinePersonAddAlt,
    MdOutlineTrendingUp,
    MdOutlineVerified,
    MdOutlineEmojiEvents,
    MdOutlineHourglassEmpty,
    MdOutlineShare,
} from 'react-icons/md';
import { useToast } from '@podus/ui';
import StatCard from '@/components/StatCard';
import RangeChips from '@/components/RangeChips';
import MetricsChart from '@/components/MetricsChart';
import ReferralLinkCard from '@/components/ReferralLinkCard';
import EmptyState from '@/components/EmptyState';
import { api, ApiError } from '@/lib/api';
import { formatNumber, formatPercent } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import type { AmbassadorProfile, LeaderboardPayload, MetricsPayload, NotificationRow, RangePreset } from '@/types';

/** The ambassador's home screen — link first, then the numbers it earned (§4.5). */
export default function Overview() {
    const toast = useToast();
    const { user } = useAuth();

    const [preset, setPreset] = useState<RangePreset>('30d');
    const [profile, setProfile] = useState<AmbassadorProfile | null>(null);
    const [metrics, setMetrics] = useState<MetricsPayload | null>(null);
    const [leaderboard, setLeaderboard] = useState<LeaderboardPayload | null>(null);
    const [notifications, setNotifications] = useState<NotificationRow[]>([]);
    const [loading, setLoading] = useState(true);

    const loadMetrics = useCallback(
        async (range: RangePreset) => {
            try {
                const res = await api.get<{ data: MetricsPayload }>(`/ambassador/me/metrics?preset=${range}`);
                setMetrics(res.response.data);
            } catch (err) {
                toast.error(err instanceof ApiError ? err.message : 'Could not load your metrics');
            }
        },
        [toast],
    );

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const [profileRes, notificationsRes, leaderboardRes] = await Promise.all([
                    api.get<{ data: AmbassadorProfile }>('/ambassador/me'),
                    api.get<{ data: NotificationRow[] }>('/ambassador/me/notifications'),
                    api.get<{ data: LeaderboardPayload }>('/ambassador/me/leaderboard'),
                ]);
                if (cancelled) return;
                setProfile(profileRes.response.data);
                setNotifications(notificationsRes.response.data ?? []);
                setLeaderboard(leaderboardRes.response.data);
            } catch (err) {
                if (!cancelled) toast.error(err instanceof ApiError ? err.message : 'Could not load your dashboard');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [toast]);

    useEffect(() => {
        void loadMetrics(preset);
    }, [preset, loadMetrics]);

    const stats = metrics?.metrics;
    const unread = notifications.filter((n) => !n.readAt);
    const hasReferrals = (stats?.signups ?? 0) > 0 || (stats?.clicks ?? 0) > 0;

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-[13px] text-[#747474]">Welcome back</p>
                    <h2 className="text-[22px] font-bold text-secondary">{profile?.name ?? user?.name ?? 'there'}</h2>
                </div>
                <RangeChips value={preset} onChange={setPreset} />
            </div>

            {/* Milestone / notice banner */}
            {unread.length > 0 && (
                <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 px-5 py-4">
                    <MdOutlineEmojiEvents size={22} className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                        <p className="text-[14px] font-bold text-secondary">{unread[0].title}</p>
                        {unread[0].body && <p className="mt-0.5 text-[13px] text-[#747474]">{unread[0].body}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            api.post('/ambassador/me/notifications/read')
                                .then(() => setNotifications((prev) => prev.map((n) => ({ ...n, readAt: 'read' }))))
                                .catch(() => toast.error('Could not dismiss that'));
                        }}
                        className="ml-auto shrink-0 text-[13px] font-semibold text-primary"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Referral link */}
            <div className="mt-5">
                {loading ? (
                    <div className="skeleton-shimmer h-40 w-full rounded-2xl" />
                ) : profile?.code ? (
                    <ReferralLinkCard code={profile.code} />
                ) : (
                    <EmptyState
                        Icon={MdOutlineShare}
                        title="Your referral link isn't ready yet"
                        description="This usually resolves itself in a moment. If it persists, contact Podus support."
                    />
                )}
            </div>

            {/* Headline metrics (FR-23) */}
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard
                    label="Link clicks"
                    value={formatNumber(stats?.clicks ?? 0)}
                    Icon={MdOutlineAdsClick}
                    loading={!stats}
                />
                <StatCard
                    label="Signups"
                    value={formatNumber(stats?.signups ?? 0)}
                    Icon={MdOutlinePersonAddAlt}
                    accent
                    loading={!stats}
                />
                <StatCard
                    label="Conversion rate"
                    value={formatPercent(stats?.conversionRate ?? 0)}
                    hint="Signups ÷ link clicks"
                    Icon={MdOutlineTrendingUp}
                    loading={!stats}
                />
                <StatCard
                    label="Activated users"
                    value={formatNumber(stats?.activatedUsers ?? 0)}
                    hint="Referrals who got started on Podus AI"
                    Icon={MdOutlineVerified}
                    loading={!stats}
                />
            </div>

            {(stats?.pendingReferrals ?? 0) > 0 && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
                    <MdOutlineHourglassEmpty size={20} className="mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-[13px] leading-5 text-amber-800">
                        <strong>{formatNumber(stats?.pendingReferrals ?? 0)}</strong> referral(s) are being reviewed
                        before they count toward your totals. Nothing is required from you.
                    </p>
                </div>
            )}

            {/* Chart (FR-24) */}
            <div className="mt-6">
                <MetricsChart data={metrics?.series ?? []} loading={!metrics} />
            </div>

            {/* Zero-state guidance (FR-27) */}
            {!loading && metrics && !hasReferrals && (
                <div className="mt-6">
                    <EmptyState
                        Icon={MdOutlineShare}
                        title="No activity yet — let's change that"
                        description="Share your link anywhere you already talk to people: WhatsApp groups, your bio, a community forum, or a printed QR code. Every signup through it is permanently credited to you."
                        action={
                            <Link
                                to="/dashboard/referrals"
                                className="inline-flex rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                            >
                                See your referrals
                            </Link>
                        }
                    />
                </div>
            )}

            {/* Leaderboard (FR-28) */}
            {leaderboard && leaderboard.totalAmbassadors > 1 && (
                <div className="mt-6 rounded-xl border border-[#E0E0E0] bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-[15px] font-bold text-secondary">Leaderboard</h3>
                        <p className="text-[13px] text-[#747474]">
                            {leaderboard.myRank
                                ? `You're #${leaderboard.myRank} of ${leaderboard.totalAmbassadors}`
                                : `${leaderboard.totalAmbassadors} ambassadors`}
                        </p>
                    </div>

                    <ul className="mt-4 divide-y divide-[#f0f0f0]">
                        {leaderboard.top.map((row) => (
                            <li
                                key={`${row.rank}-${row.name}`}
                                className={`flex items-center gap-3 py-2.5 ${row.isMe ? 'font-bold text-secondary' : 'text-[#4E4D4D]'}`}
                            >
                                <span
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${
                                        row.rank <= 3 ? 'bg-primary/10 text-primary' : 'bg-[#f5f5f5] text-[#888]'
                                    }`}
                                >
                                    {row.rank}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[14px]">
                                    {row.name}
                                    {row.isMe && <span className="ml-2 text-[12px] text-primary">you</span>}
                                </span>
                                <span className="text-[14px] tabular-nums">{formatNumber(row.signups)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
